import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ShareQuoteDialog from "@/components/ShareQuoteDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SIZES, SERVICES, newItem } from "@/components/booking/constants";
import { Loader2, Package, Plus, Trash2, MessageSquare } from "lucide-react";

export default function PartnerQuote() {
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [items, setItems] = useState([newItem()]);
  const [service, setService] = useState("standard");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [stats, setStats] = useState(null);
  const timer = useRef(null);

  const loadStats = () => api.get("/quotes/partner-stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const verified = pickup?.lat != null && pickup?.lng != null && dropoff?.lat != null && dropoff?.lng != null;

  useEffect(() => {
    if (!verified) { setQuote(null); return; }
    setQuoting(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", {
          pickup, dropoff, service,
          items: items.map((i) => ({ size: i.size, quantity: i.quantity, weight_kg: parseFloat(i.weight) || 1 })),
        });
        setQuote(data);
      } catch { setQuote(null); } finally { setQuoting(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [pickup, dropoff, items, service, verified]);

  const update = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-kilted-green sm:text-3xl" data-testid="partner-heading">Partner quote tool</h1>
        <p className="mt-1 text-sm text-muted-foreground">Price a Kilted Courier delivery and text the quote straight to your customer — they book and pay themselves.</p>

        {stats && (
          <div className="mt-6" data-testid="partner-stats">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { l: `Quotes sent · ${stats.month_label}`, v: stats.month.sent, t: "partner-stat-sent" },
                { l: "Jobs won", v: stats.month.booked, t: "partner-stat-booked" },
                { l: "Booked value", v: `$${stats.month.booked_value.toFixed(2)}`, t: "partner-stat-value" },
                { l: `Your commission (${stats.commission_pct}%)`, v: `$${stats.month.commission.toFixed(2)}`, t: "partner-stat-commission", gold: true },
              ].map((s) => (
                <div key={s.t} data-testid={s.t}
                  className={`rounded-xl border p-3 ${s.gold ? "border-kilted-gold/60 bg-kilted-gold/10" : "border-border bg-white"}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{s.l}</p>
                  <p className="mt-1 font-heading text-2xl font-extrabold text-kilted-green">{s.v}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground" data-testid="partner-stats-alltime">
              All time: {stats.all_time.sent} sent · {stats.all_time.booked} won · ${stats.all_time.booked_value.toFixed(2)} booked · ${stats.all_time.commission.toFixed(2)} commission
            </p>
          </div>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Route</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <AddressAutocomplete label="Pickup address" value={pickup} onSelect={setPickup} testid="partner-pickup" />
                <AddressAutocomplete label="Drop-off address" value={dropoff} onSelect={setDropoff} testid="partner-dropoff" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {items.map((it, idx) => (
                  <div key={it.uid} className="flex flex-wrap items-end gap-2" data-testid={`partner-item-${idx}`}>
                    <div className="w-full space-y-1 sm:w-auto sm:flex-1">
                      <Label className="text-xs">Size</Label>
                      <Select value={it.size} onValueChange={(v) => update(idx, { size: v })}>
                        <SelectTrigger data-testid={`partner-item-size-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SIZES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-[72px] space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min="1" value={it.quantity} onChange={(e) => update(idx, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })} data-testid={`partner-item-qty-${idx}`} />
                    </div>
                    <div className="min-w-[90px] flex-1 space-y-1 sm:flex-none sm:basis-[90px]">
                      <Label className="text-xs">Weight (kg)</Label>
                      <Input type="number" min="0.1" step="0.5" value={it.weight} onChange={(e) => update(idx, { weight: e.target.value })} data-testid={`partner-item-weight-${idx}`} />
                    </div>
                    <Button variant="ghost" size="icon" disabled={items.length === 1} onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))} data-testid={`partner-item-remove-${idx}`}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setItems((prev) => [...prev, newItem()])} data-testid="partner-add-item" className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Add item
                </Button>
                <div className="space-y-1 pt-2">
                  <Label className="text-xs">Service</Label>
                  <Select value={service} onValueChange={setService}>
                    <SelectTrigger data-testid="partner-service-select" className="max-w-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-20 border-kilted-green/20">
              <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Package className="h-5 w-5 text-kilted-gold" /> Quote</CardTitle></CardHeader>
              <CardContent>
                {!verified ? (
                  <p className="text-sm text-muted-foreground" data-testid="partner-quote-placeholder">Select both addresses to see the price.</p>
                ) : quoting ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : quote ? (
                  <div data-testid="partner-quote-breakdown">
                    <div className="flex items-center justify-between rounded-md bg-kilted-green px-3 py-2 text-white">
                      <span className="font-semibold">Total (inc GST)</span>
                      <span className="font-heading text-xl font-extrabold" data-testid="partner-quote-total">${quote.total.toFixed(2)}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{quote.distance_km} km · valid 7 days once sent</p>
                    <div className="mt-4">
                      <ShareQuoteDialog pickup={pickup} dropoff={dropoff} items={items} service={service} partner triggerClassName="w-full" onShared={loadStats} />
                    </div>
                    <p className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground">
                      <MessageSquare className="mt-0.5 h-3 w-3 shrink-0" /> Your customer gets an SMS with the price and a link to book and pay online.
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
